import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonNote,
  IonProgressBar,
  IonText,
} from "@ionic/react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import React, { useRef, useState } from "react";
import { ARTIST_INDEX, SONG_INDEX } from "../config/env";
import { SearchService } from "../services/SearchService";

type Target = "artists" | "songs";

interface RunState {
  target: Target;
  totalScanned: number;
  totalWritten: number;
  totalSkipped: number;
  batches: number;
  done: boolean;
  error?: string;
}

const BATCH_SIZE = 500;

const ReindexSearch: React.FC = () => {
  const [running, setRunning] = useState<Target | null>(null);
  const [state, setState] = useState<Record<Target, RunState | null>>({
    artists: null,
    songs: null,
  });
  const stopRef = useRef(false);

  const updateState = (target: Target, patch: Partial<RunState>) => {
    setState((prev) => {
      const current: RunState = prev[target] || {
        target,
        totalScanned: 0,
        totalWritten: 0,
        totalSkipped: 0,
        batches: 0,
        done: false,
      };
      return { ...prev, [target]: { ...current, ...patch } };
    });
  };

  const runReindex = async (target: Target) => {
    if (running) return;
    stopRef.current = false;
    setRunning(target);
    setState((prev) => ({
      ...prev,
      [target]: {
        target,
        totalScanned: 0,
        totalWritten: 0,
        totalSkipped: 0,
        batches: 0,
        done: false,
      },
    }));

    const db = firebase.firestore();
    const collectionName = target === "artists" ? "artists" : "songs";

    let cursor: firebase.firestore.QueryDocumentSnapshot | null = null;
    let totalScanned = 0;
    let totalWritten = 0;
    let totalSkipped = 0;
    let batches = 0;

    try {
      while (!stopRef.current) {
        let q = db
          .collection(collectionName)
          .orderBy(firebase.firestore.FieldPath.documentId())
          .limit(BATCH_SIZE);
        if (cursor) q = q.startAfter(cursor);

        const snap = await q.get();
        if (snap.empty) {
          updateState(target, { done: true });
          break;
        }

        const docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        let result: { skipped: number; written: number };
        if (target === "artists") {
          result = await SearchService.please().pushArtistBatchToIndex(docs);
        } else {
          result = await SearchService.please().pushSongBatchToIndex(docs);
        }

        totalScanned += snap.size;
        totalWritten += result.written;
        totalSkipped += result.skipped;
        batches += 1;
        cursor = snap.docs[snap.docs.length - 1];

        const done = snap.size < BATCH_SIZE;
        updateState(target, {
          totalScanned,
          totalWritten,
          totalSkipped,
          batches,
          done,
        });

        if (done) break;
      }
    } catch (err: any) {
      console.error("[ReindexSearch] error", err);
      updateState(target, { error: err?.message || "Reindex failed" });
    } finally {
      setRunning(null);
    }
  };

  const stop = () => {
    stopRef.current = true;
  };

  const renderRun = (target: Target) => {
    const s = state[target];
    if (!s) return null;
    const indexName = target === "artists" ? ARTIST_INDEX : SONG_INDEX;
    return (
      <IonCard style={{ marginTop: 12 }}>
        <IonCardHeader color={s.error ? "danger" : s.done ? "success" : "favorite"}>
          <IonCardTitle>
            {target === "artists" ? "Artists" : "Songs"} reindex
          </IonCardTitle>
          <IonCardSubtitle>Index: {indexName}</IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent>
          <IonText>
            <p>
              <strong>Batches:</strong> {s.batches}
            </p>
            <p>
              <strong>Scanned:</strong> {s.totalScanned}
            </p>
            <p>
              <strong>Pushed to Algolia:</strong> {s.totalWritten}
            </p>
            <p>
              <strong>Skipped:</strong> {s.totalSkipped}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              {s.error
                ? `Error: ${s.error}`
                : s.done
                ? "Complete"
                : running === target
                ? "Running…"
                : "Stopped"}
            </p>
          </IonText>
          {!s.done && !s.error && running === target && (
            <IonProgressBar type="indeterminate" />
          )}
        </IonCardContent>
      </IonCard>
    );
  };

  return (
    <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle>Reindex Search</IonCardTitle>
        <IonCardSubtitle>
          Push every artist and song from Firestore into Algolia in batches of {BATCH_SIZE}.
          Additive — existing index entries are not removed.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <IonItem lines="none">
          <IonLabel className="ion-text-wrap">
            <p>
              <strong>Current artist index:</strong>{" "}
              <code>{ARTIST_INDEX}</code>
            </p>
            <p>
              <strong>Current song index:</strong>{" "}
              <code>{SONG_INDEX}</code>
            </p>
            <IonNote color="medium">
              Runs entirely in your browser. Keep this tab open until each run shows
              "Complete" — closing the tab stops the reindex.
            </IonNote>
          </IonLabel>
        </IonItem>

        <IonButtons style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
          <IonButton
            fill="solid"
            color="favorite"
            disabled={!!running}
            onClick={() => runReindex("artists")}
          >
            Reindex Artists
          </IonButton>
          <IonButton
            fill="solid"
            color="favorite"
            disabled={!!running}
            onClick={() => runReindex("songs")}
          >
            Reindex Songs
          </IonButton>
          <IonButton
            fill="solid"
            color="favorite"
            disabled={!!running}
            onClick={async () => {
              await runReindex("artists");
              if (!stopRef.current) await runReindex("songs");
            }}
          >
            Reindex Both
          </IonButton>
          {running && (
            <IonButton fill="outline" color="danger" onClick={stop}>
              Stop
            </IonButton>
          )}
        </IonButtons>

        {renderRun("artists")}
        {renderRun("songs")}
      </IonCardContent>
    </IonCard>
  );
};

export default ReindexSearch;
